"use client";

import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useAppDispatch, useAppSelector } from "../redux/root";
import { readFiles, removeSingleFile } from "../redux/slices/upload.slice";
import { DriveFileUpload, FileUploadStatus } from "../redux/types/upload.types";
import { getUniqueFilename, isExecutableFile } from "../utils/common.utils";

/**
 * A global store for real File objects
 * (kept OUTSIDE Redux to avoid non-serializable warnings)
 */
const fileMapRef: { current: Map<string, File> } = {
	current: new Map(),
};

export const fileStore = {
	/** Used by saga to fetch the real File */
	getFileById: (id: string): File | null => {
		return fileMapRef.current.get(id) ?? null;
	},

	/** For cleaning up after delete */
	removeFileById: (id: string) => {
		fileMapRef.current.delete(id);
	},
};

export const useUpload = () => {
	const dispatch = useAppDispatch();

	const { files: storedFiles } = useAppSelector((state) => state.upload);

	/**
	 * Convert a File → DriveFileUpload
	 */
	const buildFileMeta = useCallback(
		(file: File, path?: string): DriveFileUpload => {
			return {
				id: uuidv4(),
				name: file.name,
				size: file.size,
				type: file.type,
				path: path || file.webkitRelativePath || "",
				progress: 0,
				status: isExecutableFile(file.name, file.type)
					? FileUploadStatus.UnsupportedFileType
					: FileUploadStatus.Queued,
			};
		},
		[]
	);

	/**
	 * Process a batch of files
	 */
	const processFiles = useCallback(
		(files: File[], basePath?: string) => {
			const metadataList: DriveFileUpload[] = [];

			// ⭐ include already stored + newly created names
			const usedNames = new Set(storedFiles.map((f) => f.name));

			files.forEach((file) => {
				const path = basePath
					? `${basePath}/${file.name}`
					: file.webkitRelativePath;

				const meta = buildFileMeta(file, path);

				// always resolve unique name against growing set
				meta.name = getUniqueFilename(meta.name, usedNames);

				usedNames.add(meta.name); // ⭐ IMPORTANT
				metadataList.push(meta);

				fileMapRef.current.set(meta.id, file);
			});

			dispatch(readFiles(metadataList));
		},
		[buildFileMeta, dispatch, storedFiles]
	);

	/**
	 * Recursively read folders dropped via drag/drop
	 */
	const scanDirectory = useCallback(
		(entry: any, path: string): Promise<File[]> => {
			return new Promise((resolve) => {
				if (entry.isFile) {
					entry.file((file: File) => {
						// Assign synthetic relative path (folder/subfolder/file)
						Object.defineProperty(file, "webkitRelativePath", {
							value: `${path}/${file.name}`,
							writable: false,
						});

						resolve([file]);
					});
				} else if (entry.isDirectory) {
					const dirReader = entry.createReader();
					const allEntries: any[] = [];

					const readBatch = () => {
						dirReader.readEntries((entries: any[]) => {
							if (entries.length === 0) {
								const filePromises = allEntries.map((ent) =>
									scanDirectory(ent, `${path}/${entry.name}`)
								);
								Promise.all(filePromises).then((nested) =>
									resolve(nested.flat())
								);
							} else {
								allEntries.push(...entries);
								readBatch();
							}
						});
					};

					readBatch();
				} else {
					resolve([]);
				}
			});
		},
		[]
	);

	/**
	 * Handle drag/drop event
	 */
	const onDropFiles = useCallback(
		async (event: React.DragEvent) => {
			event.preventDefault();

			const items = event.dataTransfer.items;
			const allFiles: File[] = [];

			for (const item of items) {
				const entry = item.webkitGetAsEntry?.();
				if (!entry) {
					continue;
				}

				if (entry.isFile) {
					const file = item.getAsFile();
					if (file) {
						allFiles.push(file);
					}
				} else if (entry.isDirectory) {
					const files = await scanDirectory(entry, "");
					allFiles.push(...files);
				}
			}

			if (allFiles.length) {
				processFiles(allFiles);
			}
		},
		[scanDirectory, processFiles]
	);

	/**
	 * Handle <input type=file multiple webkitdirectory>
	 */
	const onSelectFiles = useCallback(
		(event: any) => {
			const fileList: FileList | undefined =
				event.target.files ?? event.dataTransfer.files;
			if (!fileList) {
				return;
			}

			const files = Array.from(fileList);
			processFiles(files);
		},
		[processFiles]
	);

	/**
	 * Remove a file from Redux + local fileMap
	 */
	const clearUploadFile = useCallback(
		(file: DriveFileUpload) => {
			dispatch(removeSingleFile(file));
			fileStore.removeFileById(file.id);
		},
		[dispatch]
	);

	return {
		onSelectFiles, // supports files + folders via <input webkitdirectory>
		onDropFiles, // supports drag/drop files + folders
		processFiles,
		clearUploadFile,
		getFileById: fileStore.getFileById,
	};
};
